import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { formatGradeName } from '@/lib/grades';
import type {
  NavigationItem,
  UseReportNavigationOptions,
} from '../types/navigation';
import { buildOverviewNavigation } from '../utils/overview-navigation';
import { buildGatewayNavigation } from '../utils/gateway-navigation';
import { buildGradeLevelNavigation } from '../utils/grade-level-navigation';
import { useDownloads } from '../utils/downloads';

export function useReportNavigation(options: UseReportNavigationOptions) {
  const route = useRoute();
  const router = useRouter();
  const { fetchAllGradeDownloads, fetchGradeDownloads } = useDownloads();

  // Reactive state
  const activeItem = ref('');
  const isReady = ref(false);
  const navigationItems = ref<NavigationItem[]>([]);
  const aggregatedDownloads = ref<NavigationItem[]>([]);
  const navigationError = ref<string | null>(null);

  // Cache for gateway details
  const gatewayDetailsCache = ref<Record<string, any>>({});

  // Fetch gateway detail for a specific gateway
  const fetchGatewayDetail = async (
    reportId: string,
    grade: string,
    gatewaySlug: string,
  ): Promise<any> => {
    const cacheKey = `${reportId}-${grade}-${gatewaySlug}`;

    if (gatewayDetailsCache.value[cacheKey]) {
      return gatewayDetailsCache.value[cacheKey];
    }

    try {
      // This would need to be implemented based on your API structure
      // For now, we'll return null as the data is already available in the report
      return null;
    } catch (error) {
      // Silently fail and return null
      return null;
    }
  };

  // Build and set navigation items
  const buildAndSetNavigation = async () => {
    if (!options.report?.slug) {
      navigationError.value = 'Report data is not available';
      isReady.value = true;
      return;
    }

    const reportId = options.report.slug;
    let items: NavigationItem[] = [];

    try {
      // Build navigation based on mode

      if (options.mode === 'overview') {
        // Build overview navigation
        items = buildOverviewNavigation(reportId, options);

        // Add "Reports by Grade" section
        if (
          options.report?.report_grades &&
          options.report.report_grades.length > 0
        ) {
          const reportsByGrade: NavigationItem[] = options.report.report_grades
            .filter((grade: any) => grade.slug && grade.slug.trim())
            .sort((a: any, b: any) => a.order - b.order)
            .map((grade: any) => ({
              title: formatGradeName(
                grade.name || grade.shortcode || grade.title,
              ),
              value: `grade-${grade.slug}`,
              route: {
                name: 'report-grade-overview',
                params: {
                  id: reportId,
                  grade: grade.slug,
                },
              },
            }));

          items.push({
            title: 'Reports by Grade',
            value: 'reports-by-grade',
            isH4: true,
            isGroup: true,
            borderTop: true,
            children: reportsByGrade,
            defaultOpen: true,
          });
        }

        // Add downloads section
        try {
          const downloads = await fetchAllGradeDownloads(reportId);
          if (downloads.length > 0) {
            aggregatedDownloads.value = downloads;
            items.push({
              title: 'Publisher Response',
              value: 'downloads',
              isH4: true,
              isGroup: true,
              borderTop: true,
              children: downloads,
              defaultOpen: true,
            });
          }
        } catch (downloadError) {
          // Continue without downloads section
          // Continue without downloads section
        }
      } else if (options.mode === 'grade') {
        // Build grade level navigation
        items = buildGradeLevelNavigation(reportId, options);
      } else if (options.mode === 'gateway') {
        // Build gateway navigation
        items = buildGatewayNavigation(reportId, options);

        // Add downloads section
        if (options.gradeReport) {
          try {
            const downloads = fetchGradeDownloads(options.gradeReport);
            if (downloads.length > 0) {
              aggregatedDownloads.value = downloads;
              items.push({
                title: 'Publisher Response',
                value: 'downloads',
                isH4: true,
                isGroup: true,
                borderTop: true,
                children: downloads,
                defaultOpen: true,
              });
            }
          } catch (downloadError) {
            // Continue without downloads section
            // Continue without downloads section
          }
        }
      }

      // If no navigation items were built, add a fallback message
      if (items.length === 0) {
        items.push({
          title: 'Navigation Unavailable',
          value: 'navigation-unavailable',
          isH4: true,
          isInactive: true,
          children: [
            {
              title: 'No navigation data available for this report',
              value: 'no-data',
              isInactive: true,
            },
          ],
        });
      }

      navigationItems.value = items;
      navigationError.value = null;
      isReady.value = true;
    } catch (error) {
      // Set error message and fallback navigation
      navigationError.value =
        error instanceof Error ? error.message : 'Failed to build navigation';

      // Set fallback navigation items
      navigationItems.value = [
        {
          title: 'Navigation Error',
          value: 'navigation-error',
          isH4: true,
          isInactive: true,
          children: [
            {
              title: 'Unable to load navigation',
              value: 'error',
              isInactive: true,
            },
          ],
        },
      ];
      isReady.value = true;
    }
  };

  // Handle navigation
  const handleNavigation = (item: NavigationItem) => {
    try {
      // Don't process inactive items
      if (item.isInactive) {
        return;
      }

      activeItem.value = item.value;

      if (item.download && item.href) {
        window.open(item.href, '_blank');
        return;
      }

      if (item.href && item.href.startsWith('#')) {
        const id = route.params.id;
        if (!id || typeof id !== 'string') {
          return;
        }

        // If we're already on the correct page, just scroll to the anchor
        if (item.route?.name && route.name === item.route.name) {
          const element = document.querySelector(item.href);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          // Navigate to the page first, then scroll
          const routeName = item.route?.name || 'report-overview';
          const routeParams = { ...item.route?.params, id };

          router
            .push({
              name: routeName,
              params: routeParams,
            })
            .then(() => {
              // Wait for the page to load, then scroll
              setTimeout(() => {
                const element = document.querySelector(item.href!);
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth' });
                }
              }, 100);
            })
            .catch((error) => {
              // Navigation failed silently
            });
        }
        return;
      }

      if (item.route?.name) {
        // Handle route navigation
        const id = route.params.id;
        if (!id || typeof id !== 'string') {
          return;
        }

        router
          .push({
            name: item.route.name,
            params: { ...item.route.params, id: id as string },
          })
          .catch((error) => {
            // Navigation failed silently
          });
      }
    } catch (error) {
      // Handle navigation error silently
    }
  };

  // Watch for changes and rebuild navigation
  watch(
    () => [options.report, options.currentGatewayReport, options.gradeReport],
    (newValues, oldValues) => {
      // Only rebuild if we actually have a report and it's different
      const newReport = newValues[0];
      const oldReport = oldValues?.[0];

      if (newReport && (newReport !== oldReport || !oldReport)) {
        try {
          buildAndSetNavigation();
        } catch (error) {
          // Handle watch error silently
        }
      }
    },
    { deep: true, immediate: true },
  );

  // Initialize on mount
  onMounted(() => {
    try {
      buildAndSetNavigation();
    } catch (error) {
      // Handle mount error silently
    }
  });

  return {
    activeItem,
    isReady,
    navigationItems,
    navigationError,
    handleNavigation,
  };
}
